// Clause-scanner: tokenizes a statement from `start` to `endExclusive`,
// skipping strings/comments/quoted-identifiers, tracking paren nesting, and
// returning the dominant SQL clause at the cursor plus the last code character.
// Pure function, no tree-sitter. PRD: Slice: Position Classifier — core.

import type { ParseOptions } from '../../sql-grammar/model';
import type { ResolverPositionKind } from '../types';

export interface ClauseScanResult {
  readonly clause: ResolverPositionKind;
  readonly lastCodeChar: string | null;
}

type Frame = { clause: ResolverPositionKind; lastWasJoin: boolean };

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

type Keyword =
  | 'SELECT' | 'FROM' | 'WHERE' | 'GROUP' | 'ORDER' | 'HAVING'
  | 'JOIN' | 'ON' | 'BY';

const CLAUSE_WORDS: ReadonlySet<Keyword> = new Set<Keyword>([
  'SELECT', 'FROM', 'WHERE', 'GROUP', 'ORDER', 'HAVING', 'JOIN', 'ON', 'BY',
]);

function asKeyword(word: string): Keyword | null {
  const up = word.toUpperCase();
  return CLAUSE_WORDS.has(up as Keyword) ? (up as Keyword) : null;
}

// eslint-disable-next-line max-lines-per-function, complexity
export function scanClause(
  source: string,
  start: number,
  endExclusive: number,
  options: ParseOptions,
): ClauseScanResult {
  const engine = options.engine;
  const supportsBacktick = engine === 'mysql' || engine === 'sqlite';
  const supportsBracket = engine === 'mssql' || engine === 'sqlite';
  const supportsDollar = engine === 'postgres';
  const supportsHashComment = engine === 'mysql';

  const stack: Frame[] = [{ clause: 'unknown', lastWasJoin: false }];
  let lastCodeChar: string | null = null;
  let i = start;

  const setClause = (kind: ResolverPositionKind): void => {
    const top = stack[stack.length - 1];
    top.clause = kind;
    top.lastWasJoin = false;
  };

  while (i < endExclusive) {
    const c = source[i];
    const c2 = source[i + 1] ?? '';

    // Skip comments.
    if (c === '-' && c2 === '-') {
      i += 2;
      while (i < endExclusive && source[i] !== '\n') i++;
      continue;
    }
    if (supportsHashComment && c === '#') {
      i++;
      while (i < endExclusive && source[i] !== '\n') i++;
      continue;
    }
    if (c === '/' && c2 === '*') {
      i += 2;
      while (i < endExclusive && !(source[i] === '*' && source[i + 1] === '/')) i++;
      if (i < endExclusive) i += 2;
      continue;
    }

    // Skip string / quoted-identifier regions (don't update lastCodeChar for interior bytes).
    if (c === "'") {
      i++;
      while (i < endExclusive) {
        if (source[i] === "'") {
          if (source[i + 1] === "'") { i += 2; continue; }
          i++; break;
        }
        i++;
      }
      continue;
    }
    if (c === '"') {
      i++;
      while (i < endExclusive) {
        if (source[i] === '"') {
          if (source[i + 1] === '"') { i += 2; continue; }
          i++; break;
        }
        i++;
      }
      continue;
    }
    if (supportsBacktick && c === '`') {
      i++;
      while (i < endExclusive && source[i] !== '`') i++;
      if (i < endExclusive) i++;
      continue;
    }
    if (supportsBracket && c === '[') {
      i++;
      while (i < endExclusive && source[i] !== ']') i++;
      if (i < endExclusive) i++;
      continue;
    }
    if (supportsDollar && c === '$') {
      const match = source.slice(i).match(/^\$([A-Za-z_][A-Za-z0-9_]*)?\$/);
      if (match) {
        const tag = match[0];
        i += tag.length;
        while (i < endExclusive && !source.startsWith(tag, i)) i++;
        if (i < endExclusive) i += tag.length;
        continue;
      }
    }

    if (c === '(') {
      // Pushed paren frames default to 'subquery' — matches the DML slice's
      // contract that `SELECT * FROM t WHERE id IN (|)` classifies as subquery.
      stack.push({ clause: 'subquery', lastWasJoin: false });
      lastCodeChar = '(';
      i++;
      continue;
    }
    if (c === ')') {
      if (stack.length > 1) stack.pop();
      lastCodeChar = ')';
      i++;
      continue;
    }

    if (/\s/.test(c)) { i++; continue; }

    if (IDENT_START.test(c)) {
      const wStart = i;
      i++;
      while (i < endExclusive && IDENT_CONT.test(source[i])) i++;
      const word = source.slice(wStart, i);
      const kw = asKeyword(word);
      if (kw === 'SELECT') setClause('select-list');
      else if (kw === 'FROM') setClause('from');
      else if (kw === 'WHERE') setClause('where');
      else if (kw === 'HAVING') setClause('having');
      else if (kw === 'GROUP') setClause('group-by');
      else if (kw === 'ORDER') setClause('order-by');
      else if (kw === 'BY') { /* already folded into GROUP/ORDER */ }
      else if (kw === 'JOIN') { setClause('from'); stack[stack.length - 1].lastWasJoin = true; }
      else if (kw === 'ON') {
        if (stack[stack.length - 1].lastWasJoin) setClause('join-on');
      }
      // non-keyword identifier: track as last code char = last letter
      lastCodeChar = source[i - 1];
      continue;
    }

    // Punctuation / operator character.
    lastCodeChar = c;
    i++;
  }

  return { clause: stack[stack.length - 1].clause, lastCodeChar };
}
